import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import desmancheService from '@/services/desmanche.service';
import { fetchGarmentTypesByEdificioId } from '@/services/garmentTypes.service';

const RegistrarDesmancheModal = ({ open, onClose, onSuccess, edificios, user }) => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [tiposPrenda, setTiposPrenda] = useState([]);
    const [loadingTipos, setLoadingTipos] = useState(false);

    const [formData, setFormData] = useState({
        edificioId: '',
        tipoMancha: '',
        responsable: '',
        observaciones: '',
        prendas: [],
    });

    const [prendasSeleccionadas, setPrendasSeleccionadas] = useState([
        { tipoPrendaId: '', nombre: '', cantidad: 1 },
    ]);

    useEffect(() => {
        if (formData.edificioId) {
            // No cargar tipos de prenda para Central Housy
            if (formData.edificioId === 'central-housy') {
                setTiposPrenda([]);
                return;
            }
            loadTiposPrenda(formData.edificioId);
        }
    }, [formData.edificioId]);

    const loadTiposPrenda = async (edificioId) => {
        setLoadingTipos(true);
        try {
            const response = await fetchGarmentTypesByEdificioId(edificioId);
            setTiposPrenda(response.data || []);
        } catch (error) {
            console.error('Error loading tipos de prenda:', error);
            toast({
                title: 'Error',
                description: 'No se pudieron cargar los tipos de prenda',
                variant: 'destructive',
            });
        } finally {
            setLoadingTipos(false);
        }
    };

    const handleAddPrenda = () => {
        setPrendasSeleccionadas([
            ...prendasSeleccionadas,
            { tipoPrendaId: '', nombre: '', cantidad: 1 },
        ]);
    };

    const handleRemovePrenda = (index) => {
        setPrendasSeleccionadas(prendasSeleccionadas.filter((_, i) => i !== index));
    };

    const handlePrendaChange = (index, field, value) => {
        const updated = [...prendasSeleccionadas];
        updated[index][field] = value;

        if (field === 'tipoPrendaId') {
            const tipoPrenda = tiposPrenda.find((t) => t._id === value);
            if (tipoPrenda) {
                updated[index].nombre = tipoPrenda.nombre;
            }
        }

        setPrendasSeleccionadas(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.edificioId) {
            toast({
                title: 'Error',
                description: 'Debe seleccionar un edificio',
                variant: 'destructive',
            });
            return;
        }

        if (!formData.tipoMancha) {
            toast({
                title: 'Error',
                description: 'Debe especificar el tipo de mancha',
                variant: 'destructive',
            });
            return;
        }

        if (!formData.responsable || formData.responsable.trim() === '') {
            toast({
                title: 'Error',
                description: 'Debe especificar el nombre del responsable',
                variant: 'destructive',
            });
            return;
        }

        const prendasValidas = prendasSeleccionadas.filter(
            (p) => p.tipoPrendaId && p.cantidad > 0
        );

        if (prendasValidas.length === 0) {
            toast({
                title: 'Error',
                description: 'Debe agregar al menos una prenda',
                variant: 'destructive',
            });
            return;
        }

        // Manejar edificio especial "Central Housy" o edificio regular
        let edificioData;
        if (formData.edificioId === 'central-housy') {
            edificioData = {
                id: 'central-housy',
                nombre: 'Central Housy',
                ciudad: 'Ropa Lavada de Casa',
            };
        } else {
            const edificio = edificios.find((e) => e._id === formData.edificioId);
            edificioData = {
                id: edificio._id,
                nombre: edificio.nombre,
                ciudad: edificio.ciudad,
            };
        }

        const totalPrendas = prendasValidas.reduce((sum, p) => sum + Number(p.cantidad), 0);

        const data = {
            estado: 'En desmanche',
            prendas: prendasValidas.map((p) => ({
                nombre: p.nombre,
                id: p.tipoPrendaId,
                cantidad: Number(p.cantidad),
            })),
            totalPrendas,
            creator: {
                nombre: user?.nombre || 'Usuario',
                id: user?._id || user?.id || '',
            },
            tipoMancha: formData.tipoMancha,
            responsable: formData.responsable.trim(),
            edificio: edificioData,
            observaciones: formData.observaciones,
        };

        setLoading(true);
        try {
            await desmancheService.createDesmanche(data);
            onSuccess();
        } catch (error) {
            console.error('Error creating desmanche:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'No se pudo registrar el desmanche',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        Registrar Proceso de Desmanche
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Edificio */}
                    <div>
                        <Label htmlFor="edificio">Edificio *</Label>
                        <select
                            id="edificio"
                            className="w-full border rounded-md p-2 mt-1"
                            value={formData.edificioId}
                            onChange={(e) =>
                                setFormData({ ...formData, edificioId: e.target.value })
                            }
                            required
                        >
                            <option value="">Seleccione un edificio</option>
                            <option value="central-housy">Central Housy - Ropa Lavada de Casa</option>
                            {edificios.map((edificio) => (
                                <option key={edificio._id} value={edificio._id}>
                                    {edificio.nombre} - {edificio.ciudad}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Tipo de Mancha */}
                    <div>
                        <Label htmlFor="tipoMancha">Tipo de Mancha *</Label>
                        <select
                            id="tipoMancha"
                            className="w-full border rounded-md p-2 mt-1"
                            value={formData.tipoMancha}
                            onChange={(e) =>
                                setFormData({ ...formData, tipoMancha: e.target.value })
                            }
                            required
                        >
                            <option value="">Seleccione tipo de mancha</option>
                            <option value="arrastre">Arrastre</option>
                            <option value="amarilla">Amarilla</option>
                            <option value="grasa">Grasa</option>
                            <option value="tintas">Tintas</option>
                            <option value="oxido">Óxido</option>
                            <option value="clorhexidina">Clorhexidina</option>
                            <option value="pegante">Pegante</option>
                            <option value="azul_de_metileno">Azul de Metileno</option>
                            <option value="otro">Otro</option>
                        </select>
                    </div>

                    {/* Responsable */}
                    <div>
                        <Label htmlFor="responsable">Nombre del Responsable *</Label>
                        <Input
                            id="responsable"
                            type="text"
                            placeholder="Ingrese el nombre del responsable"
                            value={formData.responsable}
                            onChange={(e) =>
                                setFormData({ ...formData, responsable: e.target.value })
                            }
                            required
                            className="mt-1"
                        />
                    </div>

                    {/* Prendas */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label>Prendas a Desmanchar *</Label>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={handleAddPrenda}
                                disabled={!formData.edificioId}
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                Agregar Prenda
                            </Button>
                        </div>

                        {!formData.edificioId && (
                            <p className="text-sm text-gray-500 mb-2">
                                Seleccione un edificio primero
                            </p>
                        )}

                        {loadingTipos && (
                            <p className="text-sm text-gray-500 mb-2">Cargando tipos de prenda...</p>
                        )}

                        <div className="space-y-3">
                            {prendasSeleccionadas.map((prenda, index) => (
                                <div
                                    key={index}
                                    className="flex gap-2 items-start p-3 border rounded-lg bg-gray-50"
                                >
                                    <div className="flex-1">
                                        <Label className="text-xs">Tipo de Prenda</Label>
                                        {formData.edificioId === 'central-housy' ? (
                                            <Input
                                                type="text"
                                                placeholder="Nombre de la prenda"
                                                value={prenda.nombre}
                                                onChange={(e) => {
                                                    const updated = [...prendasSeleccionadas];
                                                    updated[index].nombre = e.target.value;
                                                    updated[index].tipoPrendaId = `manual-${index}`;
                                                    setPrendasSeleccionadas(updated);
                                                }}
                                                className="mt-1"
                                                required
                                            />
                                        ) : (
                                            <select
                                                className="w-full border rounded-md p-2 mt-1"
                                                value={prenda.tipoPrendaId}
                                                onChange={(e) =>
                                                    handlePrendaChange(
                                                        index,
                                                        'tipoPrendaId',
                                                        e.target.value
                                                    )
                                                }
                                                disabled={!formData.edificioId || loadingTipos}
                                            >
                                                <option value="">Seleccione...</option>
                                                {tiposPrenda.map((tipo) => (
                                                    <option key={tipo._id} value={tipo._id}>
                                                        {tipo.nombre} ({tipo.peso}g)
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="w-32">
                                        <Label className="text-xs">Cantidad</Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={prenda.cantidad}
                                            onChange={(e) =>
                                                handlePrendaChange(
                                                    index,
                                                    'cantidad',
                                                    parseInt(e.target.value) || 1
                                                )
                                            }
                                            className="mt-1"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        size="icon"
                                        variant="destructive"
                                        onClick={() => handleRemovePrenda(index)}
                                        disabled={prendasSeleccionadas.length === 1}
                                        className="mt-6"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <div className="mt-2 text-sm font-medium">
                            Total de prendas:{' '}
                            {prendasSeleccionadas.reduce(
                                (sum, p) => sum + (Number(p.cantidad) || 0),
                                0
                            )}
                        </div>
                    </div>

                    {/* Observaciones */}
                    <div>
                        <Label htmlFor="observaciones">Observaciones</Label>
                        <Textarea
                            id="observaciones"
                            value={formData.observaciones}
                            onChange={(e) =>
                                setFormData({ ...formData, observaciones: e.target.value })
                            }
                            placeholder="Observaciones adicionales sobre el proceso de desmanche..."
                            rows={3}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="bg-orange-600 hover:bg-orange-700"
                        >
                            {loading ? 'Registrando...' : 'Registrar Desmanche'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default RegistrarDesmancheModal;
